/**
 * Times, in the timezone the reader will read them (#3561).
 *
 * Two jobs here, and both exist because a maintenance banner that is wrong by
 * eight hours is worse than no banner at all.
 *
 * **Entry.** A `datetime-local` input is interpreted in the BROWSER's timezone.
 * An operator in Kuala Lumpur typing 18:00 for a window in Jakarta would save
 * an instant an hour away from the one they meant, and nothing on screen would
 * say so. `zonedWallClockToUtc` reads the typed wall clock as belonging to the
 * SELECTED zone instead, which is the only reading that matches what the
 * operator sees on the form.
 *
 * **Display.** `formatWindow` is how the banner states the window. Where the
 * browser has no letters for a zone it falls back to a UTC offset.
 */

/** The zone's offset from UTC at a given instant, in milliseconds. */
function zoneOffsetMs(at: Date, timeZone: string): number {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).formatToParts(at);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const asUtc = Date.UTC(
        get("year"),
        get("month") - 1,
        get("day"),
        get("hour") % 24,
        get("minute"),
        get("second")
    );
    return asUtc - at.getTime();
}

/**
 * "2026-09-05T18:00" read as a wall clock in `timeZone`, returned as an ISO UTC
 * instant. Empty or unparseable input returns null rather than a guess.
 *
 * Two passes, because the offset depends on the instant we are still solving
 * for: the first pass lands within an hour, the second settles it. That only
 * matters within a DST transition, which none of our zones have today — and
 * will matter the first time someone schedules against one that does.
 */
export function zonedWallClockToUtc(local: string, timeZone: string): string | null {
    const match = local.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) return null;
    const [year, month, day, hour, minute] = match.slice(1).map(Number);
    const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    let instant = wallAsUtc;
    for (let pass = 0; pass < 2; pass += 1) {
        instant = wallAsUtc - zoneOffsetMs(new Date(instant), timeZone);
    }
    return new Date(instant).toISOString();
}

/** "5 September 2026" — the date alone, with no clock in it. */
export function formatDate(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat("en-GB", {
        timeZone,
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(new Date(iso));
}

/** "6:00 PM" — no leading zero. */
function formatClock(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(new Date(iso));
}

/** "SGT" where the browser knows a name, "UTC+08:00" where it only knows a number. */
function formatZone(iso: string, timeZone: string): string {
    const short = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
        .formatToParts(new Date(iso))
        .find((p) => p.type === "timeZoneName")?.value;
    if (short && /^[A-Za-z]+$/.test(short)) return short;
    const long = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" })
        .formatToParts(new Date(iso))
        .find((p) => p.type === "timeZoneName")?.value;
    return (long ?? "UTC").replace("GMT", "UTC");
}

/**
 * The body's window. A missing end is stated as a start time and nothing more —
 * inventing a finish turns a notice into a promise nobody made.
 */
export function formatWindow(
    startsAt: string,
    endsAt: string | null,
    timeZone: string
): string {
    const date = formatDate(startsAt, timeZone);
    const zone = formatZone(startsAt, timeZone);
    if (!endsAt) return `${date}, ${formatClock(startsAt, timeZone)} (${zone})`;

    const endDate = formatDate(endsAt, timeZone);
    if (endDate === date) {
        return `${date}, ${formatClock(startsAt, timeZone)} – ${formatClock(endsAt, timeZone)} (${zone})`;
    }
    return (
        `${date}, ${formatClock(startsAt, timeZone)} – ` +
        `${endDate}, ${formatClock(endsAt, timeZone)} (${zone})`
    );
}

/** How a stored instant reads to the operator, in their own timezone. */
export function formatLocal(iso: string | null): string {
    if (!iso) return "—";
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${formatDate(iso, zone)}, ${formatClock(iso, zone)} (${formatZone(iso, zone)})`;
}

/** An ISO instant as the `datetime-local` value for a wall clock in `timeZone`. */
export function utcToZonedWallClock(iso: string | null, timeZone: string): string {
    if (!iso) return "";
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).formatToParts(new Date(iso));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
    const hour = get("hour") === "24" ? "00" : get("hour");
    return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}
